// Field geometry maths (2026-10-08): the radar's projection of real pipe
// geometry, and "Guide me" / "Directions" (bearing, distance, nearest point
// of a project's pipes, the arrival test, map-app links).
// Pure (no DOM, no imports) so frontend/tests/geoMath.test.ts runs it under
// plain Node.

export type LonLat = [number, number];
export type XY = [number, number];

const R_EARTH = 6371008.8;
const DEG = Math.PI / 180;

/** Great-circle distance in metres (haversine). */
export function distanceM(lat1: number, lon1: number, lat2: number, lon2: number): number {
  const dLat = (lat2 - lat1) * DEG;
  const dLon = (lon2 - lon1) * DEG;
  const a = Math.sin(dLat / 2) ** 2 + Math.cos(lat1 * DEG) * Math.cos(lat2 * DEG) * Math.sin(dLon / 2) ** 2;
  return 2 * R_EARTH * Math.asin(Math.min(1, Math.sqrt(a)));
}

/** Initial bearing from 1 to 2, degrees clockwise from true north, 0..360. */
export function bearingDeg(lat1: number, lon1: number, lat2: number, lon2: number): number {
  const p1 = lat1 * DEG;
  const p2 = lat2 * DEG;
  const dl = (lon2 - lon1) * DEG;
  const y = Math.sin(dl) * Math.cos(p2);
  const x = Math.cos(p1) * Math.sin(p2) - Math.sin(p1) * Math.cos(p2) * Math.cos(dl);
  return ((Math.atan2(y, x) / DEG) + 360) % 360;
}

/** Signed smallest turn from `from` to `to`, -180..180 (positive = turn right). */
export function turnDeg(to: number, from: number): number {
  return ((((to - from) % 360) + 540) % 360) - 180;
}

/** East/north metres of (lon, lat) from an origin (equirectangular: exact
 * enough within a few km, which is all the radar and the guide need). */
export function toLocal(originLat: number, originLon: number, lon: number, lat: number): XY {
  const k = DEG * R_EARTH;
  return [(lon - originLon) * k * Math.cos(originLat * DEG), (lat - originLat) * k];
}

export function fromLocal(originLat: number, originLon: number, e: number, n: number): LonLat {
  const k = DEG * R_EARTH;
  return [originLon + e / (k * Math.cos(originLat * DEG)), originLat + n / k];
}

/** Nearest point of a polyline (local metres) to the origin. */
export function nearestOnPolylineXY(pts: XY[]): { point: XY; distance: number; segment: number } | null {
  if (!pts.length) return null;
  let best: XY = pts[0];
  let bestD = Math.hypot(pts[0][0], pts[0][1]);
  let seg = 0;
  for (let i = 0; i < pts.length - 1; i++) {
    const p = pts[i];
    const q = pts[i + 1];
    const dx = q[0] - p[0];
    const dy = q[1] - p[1];
    const len2 = dx * dx + dy * dy;
    const t = len2 > 0 ? Math.max(0, Math.min(1, -(p[0] * dx + p[1] * dy) / len2)) : 0;
    const c: XY = [p[0] + t * dx, p[1] + t * dy];
    const d = Math.hypot(c[0], c[1]);
    if (d < bestD) {
      bestD = d;
      best = c;
      seg = i;
    }
  }
  return { point: best, distance: bestD, segment: seg };
}

export interface GuideTargetPath {
  id: string;
  path?: LonLat[] | null;
  lon?: number | null;
  lat?: number | null;
}

/** The nearest point (lon, lat) to the user over a set of assets (a
 * project's pipes, or one asset): lines by their nearest point, points as
 * they are. */
export function nearestTarget(lat: number, lon: number, items: GuideTargetPath[]): { id: string; lon: number; lat: number; distance: number } | null {
  let best: { id: string; lon: number; lat: number; distance: number } | null = null;
  for (const it of items) {
    let pt: LonLat | null = null;
    let d = Infinity;
    if (it.path && it.path.length) {
      const near = nearestOnPolylineXY(it.path.map((p) => toLocal(lat, lon, p[0], p[1])));
      if (near) {
        pt = fromLocal(lat, lon, near.point[0], near.point[1]);
        d = near.distance;
      }
    } else if (typeof it.lon === "number" && typeof it.lat === "number") {
      pt = [it.lon, it.lat];
      d = distanceM(lat, lon, it.lat, it.lon);
    }
    if (pt && d < (best?.distance ?? Infinity)) best = { id: it.id, lon: pt[0], lat: pt[1], distance: d };
  }
  return best;
}

/** A project's pipes in walking order: nearest-neighbour from (lat, lon),
 * each next one the nearest to the end of the last ("Next asset"). */
export function stepOrder(lat: number, lon: number, items: GuideTargetPath[]): string[] {
  const left = items.slice();
  const out: string[] = [];
  let at = { lat, lon };
  while (left.length) {
    const near = nearestTarget(at.lat, at.lon, left);
    if (!near) break;
    out.push(near.id);
    at = { lat: near.lat, lon: near.lon };
    left.splice(left.findIndex((i) => i.id === near.id), 1);
  }
  return out;
}

/** "Arrived" radius: ~10 m, or the GPS accuracy if that is worse (we can't
 * honestly say closer than the fix can tell). */
export function arrivalRadiusM(gpsAccuracy: number | null | undefined, base = 10): number {
  return Math.max(base, Number.isFinite(gpsAccuracy as number) ? (gpsAccuracy as number) : 0);
}

export interface GuideState {
  distance: number;
  bearing: number;
  /** arrow rotation on screen: bearing relative to the phone's heading (or to north) */
  arrowDeg: number;
  arrived: boolean;
  /** the remaining distance is smaller than the GPS can resolve */
  withinGpsError: boolean;
  headingKnown: boolean;
}

export function guideState(
  from: { lat: number; lon: number; accuracy?: number | null },
  to: { lat: number; lon: number },
  heading: number | null,
): GuideState {
  const distance = distanceM(from.lat, from.lon, to.lat, to.lon);
  const bearing = bearingDeg(from.lat, from.lon, to.lat, to.lon);
  const acc = from.accuracy ?? null;
  return {
    distance,
    bearing,
    arrowDeg: heading === null ? bearing : turnDeg(bearing, heading),
    arrived: distance <= arrivalRadiusM(acc),
    withinGpsError: acc !== null && distance <= acc,
    headingKnown: heading !== null,
  };
}

/** "120 m", "1.4 km" */
export function fmtDistance(m: number): string {
  if (!Number.isFinite(m)) return "—";
  if (m < 995) return `${Math.round(m)} m`;
  return `${(m / 1000).toFixed(m < 9950 ? 1 : 0)} km`;
}

export type TravelMode = "walking" | "driving";

/** Links that open the phone's own maps app at the destination (links only:
 * no embedded Google services - the Street View embed was removed for
 * licensing). Google's is the cross-platform universal URL; Apple Maps on
 * iOS; geo: hands off to whatever Android has. */
export function directionsLinks(lat: number, lon: number, mode: TravelMode, label = ""): { google: string; apple: string; geo: string } {
  const ll = `${lat.toFixed(6)},${lon.toFixed(6)}`;
  const q = label ? `(${encodeURIComponent(label)})` : "";
  return {
    google: `https://www.google.com/maps/dir/?api=1&destination=${ll}&travelmode=${mode}`,
    apple: `https://maps.apple.com/?daddr=${ll}&dirflg=${mode === "walking" ? "w" : "d"}`,
    geo: `geo:${ll}?q=${ll}${q}`,
  };
}

/** The link to use on this device (userAgent passed in, for the tests). */
export function preferredDirectionsLink(ua: string, lat: number, lon: number, mode: TravelMode, label = ""): string {
  const l = directionsLinks(lat, lon, mode, label);
  if (/iPad|iPhone|iPod|Macintosh/.test(ua)) return l.apple;
  return l.google; // Android hands this to the Maps app; desktop opens the website
}

// --- radar ----------------------------------------------------------------

/** Local east/north metres -> radar canvas px: "up" is the heading (north
 * when null), (cx, cy) the user, `scale` px per metre. */
export function radarXY(e: number, n: number, heading: number | null, scale: number, cx: number, cy: number): XY {
  const h = (heading ?? 0) * DEG;
  const x = e * Math.cos(h) - n * Math.sin(h);
  const y = e * Math.sin(h) + n * Math.cos(h);
  return [cx + x * scale, cy - y * scale];
}

/** Squared distance from point to segment (any frame). */
function seg2(px: number, py: number, a: XY, b: XY): number {
  const dx = b[0] - a[0];
  const dy = b[1] - a[1];
  const l2 = dx * dx + dy * dy;
  const t = l2 > 0 ? Math.max(0, Math.min(1, ((px - a[0]) * dx + (py - a[1]) * dy) / l2)) : 0;
  const x = a[0] + t * dx - px;
  const y = a[1] + t * dy - py;
  return x * x + y * y;
}

/** Douglas-Peucker in the polyline's own units (metres for the radar: the
 * tolerance is half a pixel at the current scale). */
export function simplify(pts: XY[], tol: number): XY[] {
  if (pts.length <= 2 || tol <= 0) return pts.slice();
  const keep = new Uint8Array(pts.length);
  keep[0] = keep[pts.length - 1] = 1;
  const stack: [number, number][] = [[0, pts.length - 1]];
  const t2 = tol * tol;
  while (stack.length) {
    const [i, j] = stack.pop() as [number, number];
    let best = -1;
    let bd = t2;
    for (let k = i + 1; k < j; k++) {
      const d = seg2(pts[k][0], pts[k][1], pts[i], pts[j]);
      if (d > bd) {
        bd = d;
        best = k;
      }
    }
    if (best >= 0) {
      keep[best] = 1;
      stack.push([i, best], [best, j]);
    }
  }
  return pts.filter((_, k) => keep[k]);
}

/** The pieces of a local-metre polyline that come within `rangeM` of the
 * user (others culled), each a run of consecutive vertices. */
export function cullToRange(pts: XY[], rangeM: number): XY[][] {
  const out: XY[][] = [];
  let run: XY[] = [];
  const r2 = rangeM * rangeM;
  for (let i = 0; i < pts.length - 1; i++) {
    const inside = seg2(0, 0, pts[i], pts[i + 1]) <= r2;
    if (inside) {
      if (!run.length) run.push(pts[i]);
      run.push(pts[i + 1]);
    } else if (run.length) {
      out.push(run);
      run = [];
    }
  }
  if (run.length) out.push(run);
  if (pts.length === 1 && Math.hypot(pts[0][0], pts[0][1]) <= rangeM) out.push([pts[0]]);
  return out;
}

/** Line width (px) for a pipe diameter: thin service pipes to fat trunk
 * mains, readable on a phone; unknown = a middling 3 px. */
export function pipeWidthPx(diameterMm: number | null | undefined): number {
  if (!diameterMm || !Number.isFinite(diameterMm) || diameterMm <= 0) return 3;
  return Math.max(2, Math.min(10, 1.5 + Math.sqrt(diameterMm) / 3.2));
}

/** Where an off-range target sits on the radar's edge (px), for the edge arrow. */
export function edgePoint(bearingRel: number, cx: number, cy: number, radiusPx: number): XY {
  const a = bearingRel * DEG;
  return [cx + Math.sin(a) * radiusPx, cy - Math.cos(a) * radiusPx];
}

/** Distance (px) from a tap to a projected polyline (for tap-to-identify on lines). */
export function pxToPolyline(x: number, y: number, pts: XY[]): number {
  if (!pts.length) return Infinity;
  if (pts.length === 1) return Math.hypot(x - pts[0][0], y - pts[0][1]);
  let best = Infinity;
  for (let i = 0; i < pts.length - 1; i++) best = Math.min(best, seg2(x, y, pts[i], pts[i + 1]));
  return Math.sqrt(best);
}

/** A nice ring spacing for a radar range: 5 / 10 / 25 / 50 m ... */
export function ringStep(rangeM: number): number {
  for (const s of [5, 10, 25, 50, 100, 250, 500]) if (rangeM / s <= 4) return s;
  return 1000;
}
